import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/hooks/useReveal";
import { api, resolveImage } from "@/lib/api";

const SoldListings = () => {
  const [sold, setSold] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/listings", {
          params: { status: "Sold", limit: 9 },
        });
        setSold(data);
      } catch {
        setSold([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section
      id="sold"
      data-testid="sold-listings-section"
      className="relative bg-[#0a0a0a] text-white py-28 md:py-40 px-6 md:px-12"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24 items-end">
          <div className="md:col-span-7">
            <Reveal>
              <p className="overline mb-6 text-[#c0c0c0]">Sold Listings</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                Quietly closed.
                <br />
                <em className="italic font-light text-[#e8e8e8]">Confidently delivered.</em>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={200}>
              <p className="text-base md:text-lg text-white/60 leading-relaxed">
                A selection from our recent transactions — many of them off-market,
                most of them never listed. Discretion isn't a feature. It's the practice.
              </p>
            </Reveal>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-white/40">Loading...</p>
        ) : sold.length === 0 ? (
          <div className="border border-dashed border-white/15 px-8 py-24 text-center">
            <p className="overline text-[#c0c0c0] mb-3">Sold archive</p>
            <p className="text-white/50 max-w-md mx-auto">
              Recent closings are being curated. Please contact us for a transaction history.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {sold.map((s, i) => (
              <Reveal key={s.id} delay={i * 120}>
                <Link
                  to={`/listings/${s.slug}`}
                  data-testid={`sold-listing-${s.id}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden aspect-[4/5] bg-black">
                    {s.image_urls?.[0] ? (
                      <img
                        src={resolveImage(s.image_urls[0])}
                        alt={s.address}
                        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[1200ms]"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute top-5 left-5 px-3 py-1 border border-white/80 text-white text-[10px] tracking-[0.32em] uppercase font-medium backdrop-blur-sm bg-black/30">
                      Sold
                    </div>
                  </div>
                  <div className="pt-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-heading text-3xl tracking-tight">{s.price || "—"}</p>
                        <p className="text-sm text-white/70 mt-1">{s.address}</p>
                        <p className="text-[11px] text-white/40 uppercase tracking-[0.22em] mt-1">
                          {[s.city, s.state].filter(Boolean).join(", ")}
                        </p>
                      </div>
                    </div>
                    <div className="mt-5 h-px bg-gradient-to-r from-[#c0c0c0]/60 via-[#c0c0c0]/20 to-transparent" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default SoldListings;
